/* GeoCommerce Exact SKU Search
   - Exact SKU only for fully numeric search terms.
   - Redirects exact variant SKU matches to /products/handle?variant=ID.
   - Shows "Invalid SKU" and blocks partial/unrelated SKU results.
   - Keeps normal keyword, typo, partial-title, and mixed text searches working. */
(function () {
  const INVALID_SKU_TEXT = 'Invalid SKU';
  const PRODUCT_CACHE = new Map();
  const LOOKUP_CACHE = new Map();
  const NUMERIC_SKU_PATTERN = /^\d+$/;

  function normalizeSku(value) {
    return (value || '').trim().toLowerCase();
  }

  function isSkuLookup(value) {
    const term = (value || '').trim();
    return term.length > 0 && NUMERIC_SKU_PATTERN.test(term);
  }

  function normalizeTitleSearchTerm(value) {
    return (value || '').trim();
  }

  function isTitleQuery(value) {
    return /^\s*title:/i.test(value || '');
  }

  function titleQueryToDisplayTerm(value) {
    return (value || '')
      .replace(/title:/gi, '')
      .replace(/\*/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function buildTitleOnlyQuery(value) {
    const term = titleQueryToDisplayTerm(normalizeTitleSearchTerm(value));
    if (!term || NUMERIC_SKU_PATTERN.test(term)) return term;

    return term
      .split(/\s+/)
      .map((part) => part.replace(/[^A-Za-z0-9'’-]/g, '').trim())
      .filter(Boolean)
      .map((part) => `title:${part}*`)
      .join(' ');
  }

  function updateSearchFormForTitleOnly(form) {
    const input = form?.querySelector('input[type="search"][name="q"]');
    if (!input) return;

    const term = normalizeTitleSearchTerm(input.value);
    if (!term || isSkuLookup(term) || isTitleQuery(term)) return;

    input.value = buildTitleOnlyQuery(term);

    let prefix = form.querySelector('input[name="options[prefix]"]');
    if (!prefix) {
      prefix = document.createElement('input');
      prefix.type = 'hidden';
      prefix.name = 'options[prefix]';
      form.appendChild(prefix);
    }
    prefix.value = 'last';

    let fields = form.querySelector('input[name="options[fields]"]');
    if (!fields) {
      fields = document.createElement('input');
      fields.type = 'hidden';
      fields.name = 'options[fields]';
      form.appendChild(fields);
    }
    fields.value = 'title';
  }

  function redirectPlainTextSearchToTitleQuery() {
    if (!window.location.pathname.includes('/search')) return false;

    const params = new URLSearchParams(window.location.search);
    const query = params.get('q') || '';
    const displayTerm = titleQueryToDisplayTerm(query);

    if (!displayTerm || isSkuLookup(displayTerm) || isTitleQuery(query)) return false;

    params.set('q', buildTitleOnlyQuery(displayTerm));
    params.set('options[prefix]', 'last');
    params.set('options[fields]', 'title');
    window.location.replace(`${window.location.pathname}?${params.toString()}${window.location.hash || ''}`);
    return true;
  }

  function productJsonUrl(productUrl) {
    if (!productUrl) return null;
    const url = new URL(productUrl, window.location.origin);
    url.search = '';
    url.hash = '';
    return `${url.pathname}.js`;
  }

  async function fetchProductJson(productUrl) {
    const url = productJsonUrl(productUrl);
    if (!url) return null;
    if (PRODUCT_CACHE.has(url)) return PRODUCT_CACHE.get(url);

    const request = fetch(url, { credentials: 'same-origin' })
      .then((response) => (response.ok ? response.json() : null))
      .catch(() => null);

    PRODUCT_CACHE.set(url, request);
    return request;
  }

  async function findExactSku(searchTerm) {
    const exactSku = normalizeSku(searchTerm);
    if (!exactSku) return null;
    if (LOOKUP_CACHE.has(exactSku)) return LOOKUP_CACHE.get(exactSku);

    const lookup = (async () => {
      const suggestUrl = new URL((window.routes && routes.predictive_search_url) || '/search/suggest', window.location.origin);
      suggestUrl.searchParams.set('q', searchTerm.trim());
      suggestUrl.searchParams.set('resources[type]', 'product');
      suggestUrl.searchParams.set('resources[limit]', '10');
      suggestUrl.searchParams.set('resources[options][fields]', 'variants.sku');

      const response = await fetch(suggestUrl.toString().replace('/search/suggest?', '/search/suggest.json?'), {
        credentials: 'same-origin',
      }).catch(() => null);

      if (!response || !response.ok) return null;

      const data = await response.json().catch(() => null);
      const products = data?.resources?.results?.products || [];

      for (const productResult of products) {
        const product = await fetchProductJson(productResult.url);
        if (!product || !Array.isArray(product.variants)) continue;

        const exactVariant = product.variants.find((variant) => normalizeSku(variant.sku) === exactSku);
        if (exactVariant) {
          return {
            product,
            variant: exactVariant,
            url: `/products/${product.handle}?variant=${exactVariant.id}`,
          };
        }
      }

      return null;
    })();

    LOOKUP_CACHE.set(exactSku, lookup);
    return lookup;
  }

  function getResultContainer(form) {
    const customElement = form.closest('predictive-search') || form.closest('main-search') || form.closest('search-form');
    return (
      customElement?.querySelector('[data-predictive-search]') ||
      form.querySelector('[data-predictive-search]') ||
      document.querySelector('[data-predictive-search]')
    );
  }

  function invalidMarkup(term) {
    const safeTerm = term.replace(/[&<>'"]/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' }[char]));
    return `
      <div id="predictive-search-results" role="listbox">
        <div id="predictive-search-results-groups-wrapper" class="predictive-search__results-groups-wrapper predictive-search__results-groups-wrapper--no-suggestions">
          <div class="predictive-search__result-group">
            <div class="predictive-search__item predictive-search__item--term" role="option" aria-selected="false">
              <div class="predictive-search__item-content predictive-search__item-content--centered">
                <p class="predictive-search__item-heading h5">${INVALID_SKU_TEXT}</p>
                <p class="predictive-search__item-vendor">No exact SKU match found for ${safeTerm}.</p>
              </div>
            </div>
          </div>
        </div>
        <span class="hidden" data-predictive-search-live-region-count-value>0</span>
      </div>`;
  }

  function showInvalidSku(form, term) {
    const container = getResultContainer(form);
    const owner = form.closest('predictive-search');

    if (container) {
      container.innerHTML = invalidMarkup(term);
      container.style.maxHeight = `${window.innerHeight}px`;
      owner?.setAttribute('results', 'true');
      owner?.setAttribute('open', 'true');
      owner?.removeAttribute('loading');
      const input = form.querySelector('input[type="search"]');
      input?.setAttribute('aria-expanded', 'true');
    } else {
      let message = form.querySelector('.gc-invalid-sku-message');
      if (!message) {
        message = document.createElement('p');
        message.className = 'gc-invalid-sku-message';
        message.setAttribute('role', 'status');
        form.appendChild(message);
      }
      message.textContent = INVALID_SKU_TEXT;
    }
  }

  function removeInvalidSku(form) {
    form.querySelector('.gc-invalid-sku-message')?.remove();
  }

  async function handleSkuSearch(form, term) {
    const match = await findExactSku(term);
    if (match?.url) {
      window.location.assign(match.url);
      return true;
    }
    showInvalidSku(form, term);
    return false;
  }


  function isTextTitleSearch(value) {
    const term = titleQueryToDisplayTerm(value).toLowerCase();
    return term.length > 0 && !NUMERIC_SKU_PATTERN.test(term);
  }

  function titleIncludesQuery(title, term) {
    return (title || '').trim().toLowerCase().includes(titleQueryToDisplayTerm(term).toLowerCase());
  }

  function getPredictiveItemTitle(item) {
    return item?.getAttribute('data-gc-product-title') || item?.querySelector('.predictive-search__item-heading')?.textContent || '';
  }

  function filterPredictiveProductsByTitle(owner, searchTerm) {
    if (!owner || !isTextTitleSearch(searchTerm)) return;

    const items = Array.from(owner.querySelectorAll('#predictive-search-results-products-list .predictive-search__list-item'));
    if (!items.length) return;

    let visibleCount = 0;
    items.forEach((item) => {
      const title = getPredictiveItemTitle(item);
      const isMatch = titleIncludesQuery(title, searchTerm);
      item.hidden = !isMatch;
      item.style.display = isMatch ? '' : 'none';
      if (isMatch) visibleCount += 1;
    });

    const liveCount = owner.querySelector('[data-predictive-search-live-region-count-value]');
    if (liveCount) liveCount.textContent = String(visibleCount);

    const groupsWrapper = owner.querySelector('#predictive-search-results-groups-wrapper');
    if (groupsWrapper) groupsWrapper.hidden = visibleCount === 0;
  }

  function filterSearchPageProductsByTitle() {
    if (!window.location.pathname.includes('/search')) return;
    const params = new URLSearchParams(window.location.search);
    const term = titleQueryToDisplayTerm(params.get('q') || '');
    if (!isTextTitleSearch(term)) return;

    const grid = document.querySelector('#product-grid');
    if (!grid) return;

    const items = Array.from(grid.querySelectorAll('li.grid__item'));
    if (!items.length) return;

    let visibleCount = 0;
    items.forEach((item) => {
      const title = item.getAttribute('data-gc-product-title') || item.querySelector('.card__heading a, .card__heading, .full-unstyled-link')?.textContent || '';
      const isMatch = titleIncludesQuery(title, term);
      item.hidden = !isMatch;
      item.style.display = isMatch ? '' : 'none';
      if (isMatch) visibleCount += 1;
    });

    let emptyMessage = document.querySelector('.gc-title-search-empty');
    if (visibleCount === 0) {
      if (!emptyMessage) {
        emptyMessage = document.createElement('div');
        emptyMessage.className = 'title-wrapper center gc-title-search-empty';
        emptyMessage.innerHTML = '<h2 class="title title--primary">No results found</h2>';
        grid.parentNode?.insertBefore(emptyMessage, grid);
      }
      emptyMessage.hidden = false;
    } else if (emptyMessage) {
      emptyMessage.hidden = true;
    }
  }


  if (window.customElements) {
    customElements.whenDefined('predictive-search').then(() => {
      const PredictiveSearchElement = customElements.get('predictive-search');
      if (!PredictiveSearchElement || PredictiveSearchElement.prototype.gcExactSkuPatched) return;

      const originalGetSearchResults = PredictiveSearchElement.prototype.getSearchResults;
      const originalRenderSearchResults = PredictiveSearchElement.prototype.renderSearchResults;
      PredictiveSearchElement.prototype.renderSearchResults = function (resultsMarkup) {
        originalRenderSearchResults.call(this, resultsMarkup);
        filterPredictiveProductsByTitle(this, this.getQuery?.() || this.input?.value || this.searchTerm || '');
      };

      PredictiveSearchElement.prototype.getSearchResults = function (searchTerm) {
        if (!isSkuLookup(searchTerm)) {
          return originalGetSearchResults.call(this, searchTerm);
        }

        const queryKey = `gc-sku-${normalizeSku(searchTerm)}`;
        this.setLiveRegionLoadingState?.();

        findExactSku(searchTerm).then((match) => {
          if (normalizeSku(this.getQuery?.() || this.input?.value) !== normalizeSku(searchTerm)) return;
          if (match?.url) {
            window.location.assign(match.url);
            return;
          }
          const markup = invalidMarkup(searchTerm);
          this.cachedResults[queryKey] = markup;
          this.renderSearchResults(markup);
        }).catch(() => {
          if (normalizeSku(this.getQuery?.() || this.input?.value) === normalizeSku(searchTerm)) {
            this.renderSearchResults(invalidMarkup(searchTerm));
          }
        });
      };

      PredictiveSearchElement.prototype.gcExactSkuPatched = true;
    });
  }

  document.addEventListener('submit', function (event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('form[role="search"], form.search')) return;

    const input = form.querySelector('input[type="search"][name="q"]');
    const term = input?.value || '';
    if (!isSkuLookup(term)) {
      updateSearchFormForTitleOnly(form);
      return;
    }

    event.preventDefault();
    removeInvalidSku(form);
    handleSkuSearch(form, term);
  }, true);

  document.addEventListener('input', function (event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.type !== 'search' || input.name !== 'q') return;
    const form = input.form;
    if (!form) return;
    removeInvalidSku(form);

    const term = input.value.trim();
    if (!isSkuLookup(term)) return;

    window.clearTimeout(input.dataset.gcSkuTimer);
    const timer = window.setTimeout(async () => {
      if (input.value.trim() !== term) return;
      const match = await findExactSku(term);
      if (!match && input.value.trim() === term) showInvalidSku(form, term);
    }, 350);
    input.dataset.gcSkuTimer = timer;
  }, true);

  async function validateSearchResultsPage() {
    if (!window.location.pathname.includes('/search')) return;
    if (redirectPlainTextSearchToTitleQuery()) return;
    const params = new URLSearchParams(window.location.search);
    const term = titleQueryToDisplayTerm(params.get('q') || '');
    if (!isSkuLookup(term)) return;

    document.documentElement.classList.add('gc-sku-search-validating');
    const match = await findExactSku(term);
    if (match?.url) {
      window.location.replace(match.url);
      return;
    }

    const grid = document.querySelector('#ProductGridContainer');
    if (grid) {
      grid.innerHTML = `<div class="template-search__results collection collection--empty page-width"><div class="title-wrapper center"><h2 class="title title--primary">${INVALID_SKU_TEXT}</h2></div></div>`;
    }
    document.documentElement.classList.remove('gc-sku-search-validating');
  }

  validateSearchResultsPage();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', filterSearchPageProductsByTitle);
  } else {
    filterSearchPageProductsByTitle();
  }

  const searchGridContainer = document.querySelector('#ProductGridContainer');
  if (searchGridContainer) {
    new MutationObserver(filterSearchPageProductsByTitle).observe(searchGridContainer, { childList: true, subtree: true });
  }
})();
